import { useEffect, useState } from "react";
import { Activity, GitBranch, KeyRound, ShieldCheck } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { getAccessToken, setAccessToken, clearAccessToken } from "../lib/access";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Brand = () => (
  <div className="hidden lg:flex flex-col justify-between bg-[var(--cc-red)] text-white p-12 w-[44%]" data-testid="access-brand-panel">
    <div className="flex items-center gap-3">
      <img src="/logo192.png" alt="CritiCalc AI" className="w-11 h-11 rounded-md ring-2 ring-white/30" />
      <span className="font-heading text-2xl font-semibold">CritiCalc AI</span>
    </div>
    <div className="space-y-8 max-w-md">
      <h1 className="font-heading text-4xl font-semibold leading-tight">Syndromic critical care, from history to treatment.</h1>
      <ul className="space-y-4 text-white/90 text-sm">
        <li className="flex gap-3"><Activity className="w-5 h-5 shrink-0" />GCS, qSOFA, SOFA, APACHE II and Shock Index, calculated instantly</li>
        <li className="flex gap-3"><GitBranch className="w-5 h-5 shrink-0" />Washington Manual-style algorithms for severity, diagnosis, treatment and prognosis</li>
        <li className="flex gap-3"><ShieldCheck className="w-5 h-5 shrink-0" />No patient data stored, ever</li>
      </ul>
    </div>
    <p className="text-xs text-white/70">Clinical decision support only. Not a substitute for physician judgement.</p>
  </div>
);

const CodeForm = ({ onUnlock }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/access`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Wrong access code");
      setAccessToken(data.token);
      onUnlock();
    } catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="w-full max-w-sm border rounded-md bg-card p-8 space-y-5 cc-rise" data-testid="access-form">
      <div className="flex items-center gap-3">
        <span className="cc-pillar-num shrink-0"><KeyRound className="w-4 h-4" /></span>
        <div>
          <div className="font-heading text-lg font-semibold">Enter access code</div>
          <div className="text-xs text-muted-foreground">Ask your unit for the code. This device is remembered for 30 days.</div>
        </div>
      </div>
      <Input data-testid="access-code-input" type="password" autoFocus autoComplete="current-password" value={code}
        onChange={(e) => setCode(e.target.value)} placeholder="Access code" className="cc-input h-11" maxLength={64} />
      {error && <div className="text-sm text-[var(--cc-red)]" data-testid="access-error">{error}</div>}
      <Button type="submit" className="cc-btn w-full" disabled={busy || !code.trim()} data-testid="access-submit">{busy ? "Checking..." : "Unlock"}</Button>
    </form>
  );
};

const LockScreen = ({ onUnlock }) => (
  <div className="min-h-screen flex bg-background cc-grain" data-testid="access-gate">
    <Brand />
    <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
      <div className="flex lg:hidden items-center gap-3">
        <img src="/logo192.png" alt="CritiCalc AI" className="w-10 h-10 rounded-md" />
        <span className="font-heading text-xl font-semibold">CritiCalc AI</span>
      </div>
      <CodeForm onUnlock={onUnlock} />
    </div>
  </div>
);

export const AccessGate = ({ children }) => {
  const [status, setStatus] = useState(getAccessToken() ? "checking" : "locked");

  useEffect(() => {
    const lock = () => { clearAccessToken(); sessionStorage.removeItem("criticalc_session"); setStatus("locked"); };
    window.addEventListener("cc-locked", lock);
    if (status === "checking") {
      fetch(`${API}/access/check`, { headers: { "X-Access-Token": getAccessToken() } })
        .then((r) => (r.ok ? setStatus("open") : lock()))
        .catch(() => setStatus("open"));
    }
    return () => window.removeEventListener("cc-locked", lock);
  }, [status]);

  if (status === "checking") return null;
  if (status === "locked") return <LockScreen onUnlock={() => setStatus("open")} />;
  return children;
};
